'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import { fetchMarketFundamentals, fetchStormIntelligence, fetchCompetitiveLandscape, fetchOpenbooqIntelligence, fetchLaunchPlanning } from '@/lib/ai/geminiCache';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
export type ExpansionStage = 'Researching' | 'Approved' | 'Launching' | 'Active';

export interface ExpansionPriorityItem {
  id: string;
  rank: number;
  city: string;
  state: string;
  stage: ExpansionStage;
  owner: string;
  targetQuarter: string;
  aiJustification: string;
  justificationLoading: boolean;
  notes: string;
}

interface ExpansionPriorityListProps {
  initialItems?: ExpansionPriorityItem[];
  onItemsChange?: (items: ExpansionPriorityItem[]) => void;
}

// ─── Stage config ─────────────────────────────────────────────────────────────
const STAGE_CONFIG: Record<ExpansionStage, { color: string; bg: string; dot: string }> = {
  Researching: { color: '#F59E0B', bg: '#FEF3C7', dot: '#F59E0B' },
  Approved:    { color: '#E89A3C', bg: '#EFF6FF', dot: '#E89A3C' },
  Launching:   { color: '#8B5CF6', bg: '#F5F3FF', dot: '#8B5CF6' },
  Active:      { color: '#10B981', bg: '#ECFDF5', dot: '#10B981' },
};

const STAGES: ExpansionStage[] = ['Researching', 'Approved', 'Launching', 'Active'];

const QUARTERS = ['Q1 2025', 'Q2 2025', 'Q3 2025', 'Q4 2025', 'Q1 2026', 'Q2 2026', 'Q3 2026', 'Q4 2026', 'Q1 2027', 'Q2 2027'];

const TEAM_MEMBERS = ['Unassigned', 'Alex Johnson', 'Maria Garcia', 'David Kim', 'Sarah Chen', 'Mike Torres', 'Lisa Park'];

// ─── Supabase helpers ─────────────────────────────────────────────────────────
function rowToItem(row: any): ExpansionPriorityItem {
  return {
    id: row.id,
    rank: row.rank,
    city: row.city,
    state: row.state ?? '',
    stage: row.stage as ExpansionStage,
    owner: row.owner,
    targetQuarter: row.target_quarter,
    aiJustification: row.ai_justification ?? '',
    justificationLoading: false,
    notes: row.notes ?? '',
  };
}

async function loadItemsFromDB(): Promise<ExpansionPriorityItem[] | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('expansion_priority_list')
    .select('*')
    .order('rank', { ascending: true });
  if (error) return null;
  return (data ?? []).map(rowToItem);
}

async function saveItemToDB(item: ExpansionPriorityItem): Promise<void> {
  const supabase = createClient();
  await supabase.from('expansion_priority_list').upsert({
    id: item.id,
    rank: item.rank,
    city: item.city,
    state: item.state,
    stage: item.stage,
    owner: item.owner,
    target_quarter: item.targetQuarter,
    ai_justification: item.aiJustification,
    notes: item.notes,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'id' });
}

async function deleteItemFromDB(id: string): Promise<void> {
  const supabase = createClient();
  await supabase.from('expansion_priority_list').delete().eq('id', id);
}

async function saveAllRanksToDB(items: ExpansionPriorityItem[]): Promise<void> {
  const supabase = createClient();
  await supabase.from('expansion_priority_list').upsert(
    items.map((item) => ({
      id: item.id,
      rank: item.rank,
      city: item.city,
      state: item.state,
      stage: item.stage,
      owner: item.owner,
      target_quarter: item.targetQuarter,
      ai_justification: item.aiJustification,
      notes: item.notes,
      updated_at: new Date().toISOString(),
    })),
    { onConflict: 'id' }
  );
}

// ─── PDF Export helper ────────────────────────────────────────────────────────
function exportToPDF(items: ExpansionPriorityItem[]) {
  const stageColors: Record<ExpansionStage, string> = {
    Researching: '#F59E0B',
    Approved: '#E89A3C',
    Launching: '#8B5CF6',
    Active: '#10B981',
  };

  const rows = items
    .map(
      (item) => `
    <tr style="border-bottom:1px solid #F3F4F6;">
      <td style="padding:12px 16px;font-weight:700;color:#1C2E5E;font-size:14px;">#${item.rank}</td>
      <td style="padding:12px 16px;">
        <div style="font-weight:700;color:#1C2E5E;font-size:14px;">${item.city}, ${item.state}</div>
      </td>
      <td style="padding:12px 16px;">
        <span style="background:${stageColors[item.stage]}22;color:${stageColors[item.stage]};font-weight:700;font-size:11px;padding:3px 10px;border-radius:20px;border:1.5px solid ${stageColors[item.stage]}44;">
          ${item.stage}
        </span>
      </td>
      <td style="padding:12px 16px;color:#374151;font-size:13px;">${item.owner}</td>
      <td style="padding:12px 16px;color:#374151;font-size:13px;">${item.targetQuarter}</td>
      <td style="padding:12px 16px;color:#6B7280;font-size:12px;max-width:280px;">${item.aiJustification || '—'}</td>
    </tr>`
    )
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Expansion Priority Roadmap — NextRoof 360</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 40px; background: #fff; }
    h1 { color: #1C2E5E; font-size: 24px; margin: 0 0 4px; }
    .subtitle { color: #6B7280; font-size: 13px; margin: 0 0 32px; }
    table { width: 100%; border-collapse: collapse; }
    thead tr { background: linear-gradient(135deg, #1C2E5E, #1C2E5E); }
    thead th { color: #fff; text-align: left; padding: 12px 16px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
    .footer { margin-top: 32px; color: #9CA3AF; font-size: 11px; }
  </style>
</head>
<body>
  <h1>🗺️ Expansion Priority Roadmap</h1>
  <p class="subtitle">Generated by NextRoof 360 Market Intelligence · ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
  <table>
    <thead>
      <tr>
        <th>Rank</th>
        <th>Market</th>
        <th>Stage</th>
        <th>Owner</th>
        <th>Target Quarter</th>
        <th>AI Justification</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">NextRoof 360 · Market Expansion Intelligence · Confidential</div>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `NextRoof 360-Expansion-Roadmap-${new Date().toISOString().slice(0, 10)}.html`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Market Fundamentals Panel (Priority List) ────────────────────────────────
function MarketFundamentalsInline({ city, state, itemId }: { city: string; state: string; itemId: string }) {
  const [data, setData] = useState<{
    totalAddressableMarket: string;
    medianHomeAge: string;
    homeownershipRate: string;
    avgHomeValueRange: string;
    populationGrowthTrend: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    fetchMarketFundamentals(itemId, city, state)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [itemId, city, state]);

  const rows = data ? [
    { icon: 'BuildingOffice2Icon', label: 'Total Addressable Market', value: data.totalAddressableMarket },
    { icon: 'HomeIcon', label: 'Median Home Age', value: data.medianHomeAge },
    { icon: 'UserGroupIcon', label: 'Homeownership Rate', value: data.homeownershipRate },
    { icon: 'CurrencyDollarIcon', label: 'Avg Home Value Range', value: data.avgHomeValueRange },
    { icon: 'ArrowTrendingUpIcon', label: 'Population Growth Trend', value: data.populationGrowthTrend },
  ] : [];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E0E7FF' }}>
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)' }}>
        <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #4338CA 100%)' }}>
          <Icon name="ChartBarIcon" size={10} className="text-white" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#3730A3' }}>Market Fundamentals</span>
        {loading && <div className="w-3 h-3 rounded-full border border-[#6366F1] border-t-transparent animate-spin ml-auto" />}
      </div>
      <div className="divide-y" style={{ divideColor: '#F3F4F6' }}>
        {loading ? (
          <div className="p-3 space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#E0E7FF', width: '45%' }} />
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#E0E7FF', width: '30%' }} />
              </div>
            ))}
          </div>
        ) : rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2" style={{ background: i % 2 === 0 ? '#FAFAFA' : '#fff' }}>
            <div className="flex items-center gap-1.5">
              <Icon name={row.icon as any} size={11} style={{ color: '#6366F1' }} />
              <span className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{row.label}</span>
            </div>
            <span className="text-[10px] font-bold text-right ml-2" style={{ color: '#1E1B4B', maxWidth: '55%' }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Storm Intelligence Panel (Priority List) ─────────────────────────────────
function StormIntelligenceInline({ city, state, itemId }: { city: string; state: string; itemId: string }) {
  const [data, setData] = useState<{
    hailEventFrequency: string;
    lastMajorStormEvent: string;
    stormSeasonPeakMonths: string;
    insuranceClaimDensity: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    fetchStormIntelligence(itemId, city, state)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [itemId, city, state]);

  const rows = data ? [
    { icon: 'CloudIcon', label: 'Hail Event Frequency', value: data.hailEventFrequency, accent: '#0EA5E9' },
    { icon: 'BoltIcon', label: 'Last Major Storm Event', value: data.lastMajorStormEvent, accent: '#F59E0B' },
    { icon: 'CalendarDaysIcon', label: 'Storm Season Peak Months', value: data.stormSeasonPeakMonths, accent: '#8B5CF6' },
    { icon: 'DocumentTextIcon', label: 'Insurance Claim Density', value: data.insuranceClaimDensity, accent: '#EF4444' },
  ] : [];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #BAE6FD' }}>
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)' }}>
        <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #0369A1 100%)' }}>
          <Icon name="CloudIcon" size={10} className="text-white" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#0C4A6E' }}>Storm Intelligence</span>
        {loading && <div className="w-3 h-3 rounded-full border border-[#0EA5E9] border-t-transparent animate-spin ml-auto" />}
      </div>
      <div className="divide-y" style={{ divideColor: '#F3F4F6' }}>
        {loading ? (
          <div className="p-3 space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#BAE6FD', width: '45%' }} />
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#BAE6FD', width: '30%' }} />
              </div>
            ))}
          </div>
        ) : rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2" style={{ background: i % 2 === 0 ? '#F0F9FF' : '#fff' }}>
            <div className="flex items-center gap-1.5">
              <Icon name={row.icon as any} size={11} style={{ color: row.accent }} />
              <span className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{row.label}</span>
            </div>
            <span className="text-[10px] font-bold text-right ml-2" style={{ color: '#0C4A6E', maxWidth: '55%' }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Competitive Landscape Panel (Priority List) ──────────────────────────────
function CompetitiveLandscapeInline({ city, state, itemId }: { city: string; state: string; itemId: string }) {
  const [data, setData] = useState<{
    estimatedContractors: string;
    marketSaturationScore: string;
    dominantCompetitors: string;
    avgCompetitorPricing: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    fetchCompetitiveLandscape(itemId, city, state)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [itemId, city, state]);

  const rows = data ? [
    { icon: 'BuildingStorefrontIcon', label: 'Est. Roofing Contractors', value: data.estimatedContractors, accent: '#F59E0B' },
    { icon: 'SignalIcon', label: 'Market Saturation Score', value: data.marketSaturationScore, accent: '#EF4444' },
    { icon: 'TrophyIcon', label: 'Dominant Competitors', value: data.dominantCompetitors, accent: '#8B5CF6' },
    { icon: 'CurrencyDollarIcon', label: 'Avg Competitor Pricing', value: data.avgCompetitorPricing, accent: '#10B981' },
  ] : [];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #FDE68A' }}>
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)' }}>
        <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}>
          <Icon name="TrophyIcon" size={10} className="text-white" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#92400E' }}>Competitive Landscape</span>
        {loading && <div className="w-3 h-3 rounded-full border border-[#F59E0B] border-t-transparent animate-spin ml-auto" />}
      </div>
      <div className="divide-y" style={{ divideColor: '#F3F4F6' }}>
        {loading ? (
          <div className="p-3 space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#FDE68A', width: '45%' }} />
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#FDE68A', width: '35%' }} />
              </div>
            ))}
          </div>
        ) : rows.map((row, i) => (
          <div key={i} className="flex items-start justify-between px-3 py-2" style={{ background: i % 2 === 0 ? '#FFFDF5' : '#fff' }}>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Icon name={row.icon as any} size={11} style={{ color: row.accent }} />
              <span className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{row.label}</span>
            </div>
            <span className="text-[10px] font-bold text-right ml-2" style={{ color: '#78350F', maxWidth: '55%' }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── NextRoof 360 Intelligence Panel (Priority List) ──────────────────────────────
function OpenbooqIntelligenceInline({ city, state, itemId }: { city: string; state: string; itemId: string }) {
  const [data, setData] = useState<{
    estimatedRampTime: string;
    recommendedCrewSize: string;
    suggestedLeadSourceMix: string;
    riskFlags: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    fetchOpenbooqIntelligence(itemId, city, state)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [itemId, city, state]);

  const rows = data ? [
    { icon: 'ClockIcon', label: 'Estimated Ramp Time', value: data.estimatedRampTime, accent: '#6366F1' },
    { icon: 'UserGroupIcon', label: 'Recommended Crew Size', value: data.recommendedCrewSize, accent: '#10B981' },
    { icon: 'MegaphoneIcon', label: 'Suggested Lead Source Mix', value: data.suggestedLeadSourceMix, accent: '#E89A3C' },
    { icon: 'ExclamationTriangleIcon', label: 'Risk Flags', value: data.riskFlags, accent: '#EF4444' },
  ] : [];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #C7D2FE' }}>
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)' }}>
        <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}>
          <Icon name="RocketLaunchIcon" size={10} className="text-white" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>NextRoof 360 Intelligence</span>
        {loading && <div className="w-3 h-3 rounded-full border border-[#6366F1] border-t-transparent animate-spin ml-auto" />}
      </div>
      <div className="divide-y" style={{ divideColor: '#F3F4F6' }}>
        {loading ? (
          <div className="p-3 space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#C7D2FE', width: '45%' }} />
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#C7D2FE', width: '35%' }} />
              </div>
            ))}
          </div>
        ) : rows.map((row, i) => (
          <div key={i} className="flex items-start justify-between px-3 py-2" style={{ background: i % 2 === 0 ? '#F8F9FF' : '#fff' }}>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Icon name={row.icon as any} size={11} style={{ color: row.accent }} />
              <span className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{row.label}</span>
            </div>
            <span className="text-[10px] font-bold text-right ml-2" style={{ color: '#1E1B4B', maxWidth: '55%' }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Launch Planning Panel (Priority List) ────────────────────────────────────
function LaunchPlanningInline({ city, state, itemId }: { city: string; state: string; itemId: string }) {
  const [data, setData] = useState<{
    recommendedLaunchWindow: string;
    goNoGoScore: string;
    confidenceLevel: string;
    nextActions: [string, string, string];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    fetchLaunchPlanning(itemId, city, state)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [itemId, city, state]);

  const goNoGoColor = data
    ? data.goNoGoScore.toUpperCase().includes('NO-GO')
      ? '#EF4444' : data.goNoGoScore.toUpperCase().includes('CONDITIONAL')
      ? '#F59E0B' :'#10B981' :'#6B7280';

  const goNoGoBg = data
    ? data.goNoGoScore.toUpperCase().includes('NO-GO')
      ? '#FEF2F2' : data.goNoGoScore.toUpperCase().includes('CONDITIONAL')
      ? '#FFFBEB' :'#ECFDF5' :'#F9FAFB';

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #BBF7D0' }}>
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' }}>
        <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
          <Icon name="RocketLaunchIcon" size={10} className="text-white" />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#065F46' }}>Launch Planning</span>
        {loading && <div className="w-3 h-3 rounded-full border border-[#10B981] border-t-transparent animate-spin ml-auto" />}
      </div>

      {loading ? (
        <div className="p-3 space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex justify-between items-center">
              <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#BBF7D0', width: '45%' }} />
              <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#BBF7D0', width: '30%' }} />
            </div>
          ))}
        </div>
      ) : data ? (
        <div className="p-3 space-y-2.5">
          {/* Launch Window */}
          <div className="flex items-start justify-between px-3 py-2 rounded-lg" style={{ background: '#F0FDF4' }}>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Icon name="CalendarDaysIcon" size={11} style={{ color: '#10B981' }} />
              <span className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Recommended Launch Window</span>
            </div>
            <span className="text-[10px] font-bold text-right ml-2" style={{ color: '#065F46', maxWidth: '55%' }}>{data.recommendedLaunchWindow}</span>
          </div>

          {/* Go/No-Go Score */}
          <div className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: goNoGoBg }}>
            <div className="flex items-center gap-1.5">
              <Icon name="CheckCircleIcon" size={11} style={{ color: goNoGoColor }} />
              <span className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Go/No-Go Verdict</span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="text-[10px] font-black px-2 py-0.5 rounded-full"
                style={{ background: goNoGoColor + '22', color: goNoGoColor, border: `1.5px solid ${goNoGoColor}44` }}
              >
                {data.goNoGoScore}
              </span>
              <span className="text-[10px] font-semibold" style={{ color: '#6B7280' }}>{data.confidenceLevel}</span>
            </div>
          </div>

          {/* Next 3 Actions */}
          <div>
            <div className="flex items-center gap-1.5 px-1 mb-1.5">
              <Icon name="ListBulletIcon" size={11} style={{ color: '#10B981' }} />
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#065F46' }}>Next 3 Actions</span>
            </div>
            <div className="space-y-1.5">
              {data.nextActions.map((action, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-lg" style={{ background: i % 2 === 0 ? '#F0FDF4' : '#fff', border: '1px solid #D1FAE5' }}>
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[9px] font-black text-white"
                    style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                  >
                    {i + 1}
                  </div>
                  <span className="text-[10px] font-medium leading-relaxed" style={{ color: '#065F46' }}>{action}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── Priority Item Row ────────────────────────────────────────────────────────
interface PriorityItemRowProps {
  item: ExpansionPriorityItem;
  isDragging: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onUpdate: (updates: Partial<ExpansionPriorityItem>) => void;
  onDelete: () => void;
  onGenerateJustification: () => void;
}

function PriorityItemRow({
  item,
  isDragging,
  onDragStart,
  onDragOver,
  onDrop,
  onUpdate,
  onDelete,
  onGenerateJustification,
}: PriorityItemRowProps) {
  const [expanded, setExpanded] = useState(false);
  const stageCfg = STAGE_CONFIG[item.stage];

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => { e.preventDefault(); onDragOver(e); }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      className="rounded-2xl overflow-hidden transition-all"
      style={{
        background: '#fff',
        border: `1.5px solid ${isDragging ? '#E89A3C' : '#E5E7EB'}`,
        boxShadow: isDragging ? '0 8px 24px rgba(232,154,60,0.2)' : '0 1px 4px rgba(0,0,0,0.04)',
        opacity: isDragging ? 0.7 : 1,
        cursor: 'grab',
      }}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Drag handle + rank */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div style={{ color: '#D1D5DB', cursor: 'grab' }}>
            <Icon name="Bars3Icon" size={16} />
          </div>
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}
          >
            {item.rank}
          </div>
        </div>

        {/* City + State */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold" style={{ color: '#1C2E5E' }}>
              {item.city}{item.state ? `, ${item.state}` : ''}
            </span>
            {/* Stage badge */}
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: stageCfg.bg, color: stageCfg.color, border: `1.5px solid ${stageCfg.color}44` }}
            >
              {item.stage}
            </span>
          </div>
          {/* AI justification preview */}
          {item.aiJustification && !expanded && (
            <p className="text-[11px] mt-0.5 truncate" style={{ color: '#6B7280' }}>
              {item.aiJustification}
            </p>
          )}
        </div>

        {/* Owner + Quarter (compact) */}
        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Owner</div>
            <div className="text-xs font-semibold" style={{ color: '#374151' }}>{item.owner}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Target</div>
            <div className="text-xs font-semibold" style={{ color: '#374151' }}>{item.targetQuarter}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onGenerateJustification}
            disabled={item.justificationLoading}
            title="Generate AI justification"
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-blue-50"
            style={{ color: '#E89A3C' }}
          >
            {item.justificationLoading ? (
              <div className="w-3.5 h-3.5 rounded-full border-2 border-[#E89A3C] border-t-transparent animate-spin" />
            ) : (
              <Icon name="SparklesIcon" size={14} />
            )}
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-gray-100"
            style={{ color: '#9CA3AF' }}
          >
            <Icon name={expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={14} />
          </button>
          <button
            onClick={onDelete}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-red-50"
            style={{ color: '#EF4444' }}
          >
            <Icon name="TrashIcon" size={13} />
          </button>
        </div>
      </div>

      {/* Stage progress bar */}
      <div className="px-4 pb-2">
        <div className="flex items-center gap-1">
          {STAGES.map((stage, idx) => {
            const stageIdx = STAGES.indexOf(item.stage);
            const isActive = idx <= stageIdx;
            const isCurrent = stage === item.stage;
            const cfg = STAGE_CONFIG[stage];
            return (
              <button
                key={stage}
                onClick={() => onUpdate({ stage })}
                className="flex-1 flex flex-col items-center gap-1 group"
              >
                <div
                  className="w-full h-1.5 rounded-full transition-all"
                  style={{ background: isActive ? cfg.color : '#F3F4F6' }}
                />
                <span
                  className="text-[9px] font-semibold transition-all"
                  style={{ color: isCurrent ? cfg.color : '#D1D5DB' }}
                >
                  {stage}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: '#F3F4F6' }}>
          <div className="pt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Stage selector */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide mb-1.5" style={{ color: '#6B7280' }}>Stage</label>
              <select
                value={item.stage}
                onChange={(e) => onUpdate({ stage: e.target.value as ExpansionStage })}
                className="w-full px-3 py-2 rounded-xl text-xs border outline-none"
                style={{ borderColor: '#E5E7EB', color: '#1C2E5E', background: '#fff' }}
              >
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {/* Owner */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide mb-1.5" style={{ color: '#6B7280' }}>Owner</label>
              <select
                value={item.owner}
                onChange={(e) => onUpdate({ owner: e.target.value })}
                className="w-full px-3 py-2 rounded-xl text-xs border outline-none"
                style={{ borderColor: '#E5E7EB', color: '#1C2E5E', background: '#fff' }}
              >
                {TEAM_MEMBERS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {/* Target Quarter */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wide mb-1.5" style={{ color: '#6B7280' }}>Target Quarter</label>
              <select
                value={item.targetQuarter}
                onChange={(e) => onUpdate({ targetQuarter: e.target.value })}
                className="w-full px-3 py-2 rounded-xl text-xs border outline-none"
                style={{ borderColor: '#E5E7EB', color: '#1C2E5E', background: '#fff' }}
              >
                {QUARTERS.map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
          </div>

          {/* AI Justification */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#6B7280' }}>AI Priority Justification</label>
              <button
                onClick={onGenerateJustification}
                disabled={item.justificationLoading}
                className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-lg transition-all hover:opacity-80"
                style={{ background: '#EFF6FF', color: '#1C2E5E' }}
              >
                <Icon name="SparklesIcon" size={10} />
                {item.justificationLoading ? 'Generating...' : 'Regenerate'}
              </button>
            </div>
            {item.justificationLoading ? (
              <div className="space-y-1.5 rounded-xl p-3" style={{ background: '#F9FAFB' }}>
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#E5E7EB', width: '90%' }} />
                <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#E5E7EB', width: '75%' }} />
              </div>
            ) : item.aiJustification ? (
              <div
                className="rounded-xl p-3 text-xs leading-relaxed"
                style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE', color: '#1E3A5F' }}
              >
                {item.aiJustification}
              </div>
            ) : (
              <button
                onClick={onGenerateJustification}
                className="w-full rounded-xl p-3 text-xs font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-80"
                style={{ background: '#F9FAFB', border: '2px dashed #E5E7EB', color: '#9CA3AF' }}
              >
                <Icon name="SparklesIcon" size={13} />
                Click to generate AI justification
              </button>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide mb-1.5" style={{ color: '#6B7280' }}>Notes</label>
            <textarea
              value={item.notes}
              onChange={(e) => onUpdate({ notes: e.target.value })}
              placeholder="Add internal notes..."
              rows={2}
              className="w-full px-3 py-2 rounded-xl text-xs border outline-none resize-none"
              style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
            />
          </div>

          {/* Market Fundamentals */}
          <MarketFundamentalsInline city={item.city} state={item.state} itemId={item.id} />

          {/* Storm Intelligence */}
          <StormIntelligenceInline city={item.city} state={item.state} itemId={item.id} />

          {/* Competitive Landscape */}
          <CompetitiveLandscapeInline city={item.city} state={item.state} itemId={item.id} />

          {/* NextRoof 360 Intelligence */}
          <OpenbooqIntelligenceInline city={item.city} state={item.state} itemId={item.id} />

          {/* Launch Planning */}
          <LaunchPlanningInline city={item.city} state={item.state} itemId={item.id} />
        </div>
      )}
    </div>
  );
}

// ─── Add Market Form ──────────────────────────────────────────────────────────
interface AddMarketFormProps {
  onAdd: (city: string, state: string) => void;
}

function AddMarketForm({ onAdd }: AddMarketFormProps) {
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  const handleAdd = () => {
    if (!city.trim()) return;
    onAdd(city.trim(), state.trim().toUpperCase().slice(0, 2));
    setCity('');
    setState('');
  };

  return (
    <div
      className="rounded-2xl p-4 flex items-center gap-3"
      style={{ background: '#F9FAFB', border: '2px dashed #E5E7EB' }}
    >
      <div className="flex-1 flex items-center gap-2">
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="City name"
          className="flex-1 px-3 py-2 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-[#E89A3C]/30"
          style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
        />
        <input
          type="text"
          value={state}
          onChange={(e) => setState(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="ST"
          maxLength={2}
          className="w-16 px-3 py-2 rounded-xl text-sm border outline-none focus:ring-2 focus:ring-[#E89A3C]/30 uppercase"
          style={{ borderColor: '#E5E7EB', color: '#1C2E5E' }}
        />
      </div>
      <button
        onClick={handleAdd}
        disabled={!city.trim()}
        className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
        style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
      >
        <Icon name="PlusIcon" size={13} />
        Add Market
      </button>
    </div>
  );
}

// ─── Stage Summary Bar ────────────────────────────────────────────────────────
function StageSummaryBar({ items }: { items: ExpansionPriorityItem[] }) {
  const counts = STAGES.reduce((acc, s) => {
    acc[s] = items.filter((i) => i.stage === s).length;
    return acc;
  }, {} as Record<ExpansionStage, number>);

  return (
    <div className="grid grid-cols-4 gap-2">
      {STAGES.map((stage) => {
        const cfg = STAGE_CONFIG[stage];
        return (
          <div
            key={stage}
            className="rounded-xl p-3 text-center"
            style={{ background: cfg.bg, border: `1px solid ${cfg.color}33` }}
          >
            <div className="text-xl font-black" style={{ color: cfg.color }}>{counts[stage]}</div>
            <div className="text-[10px] font-semibold mt-0.5" style={{ color: cfg.color }}>{stage}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ExpansionPriorityList({
  initialItems,
  onItemsChange,
}: ExpansionPriorityListProps) {
  const [items, setItems] = useState<ExpansionPriorityItem[]>([]);
  const [dbLoading, setDbLoading] = useState(true);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const idCounter = useRef(100);
  // Debounce timer for field updates
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load from Supabase on mount
  useEffect(() => {
    loadItemsFromDB().then((dbItems) => {
      if (dbItems && dbItems.length > 0) {
        setItems(dbItems);
      } else if (initialItems && initialItems.length > 0) {
        setItems(initialItems);
      }
      setDbLoading(false);
    }).catch(() => {
      if (initialItems && initialItems.length > 0) {
        setItems(initialItems);
      }
      setDbLoading(false);
    });
  }, []);

  const updateItems = useCallback(
    (newItems: ExpansionPriorityItem[]) => {
      const ranked = newItems.map((item, idx) => ({ ...item, rank: idx + 1 }));
      setItems(ranked);
      onItemsChange?.(ranked);
      // Save all ranks to DB (for drag reorder)
      saveAllRanksToDB(ranked).catch(() => {});
    },
    [onItemsChange]
  );

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const newItems = [...items];
    const [moved] = newItems.splice(dragIndex, 1);
    newItems.splice(targetIndex, 0, moved);
    setDragIndex(null);
    updateItems(newItems);
  };

  const handleUpdate = useCallback((id: string, updates: Partial<ExpansionPriorityItem>) => {
    setItems((prev) => {
      const updated = prev.map((item) => (item.id === id ? { ...item, ...updates } : item));
      // Debounce save to avoid too many DB calls on rapid typing
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const target = updated.find((i) => i.id === id);
        if (target) saveItemToDB(target).catch(() => {});
      }, 600);
      return updated;
    });
  }, []);

  const handleDelete = useCallback((id: string) => {
    setItems((prev) => {
      const filtered = prev.filter((item) => item.id !== id);
      const ranked = filtered.map((item, idx) => ({ ...item, rank: idx + 1 }));
      // Delete from DB then re-save ranks
      deleteItemFromDB(id).then(() => saveAllRanksToDB(ranked)).catch(() => {});
      // Schedule parent notification after render to avoid "update during render" error
      setTimeout(() => onItemsChange?.(ranked), 0);
      return ranked;
    });
  }, [onItemsChange]);

  const handleAdd = useCallback((city: string, state: string) => {
    const newItem: ExpansionPriorityItem = {
      id: crypto.randomUUID ? crypto.randomUUID() : `item-${++idCounter.current}-${Date.now()}`,
      rank: 0, // will be set after re-rank
      city,
      state,
      stage: 'Researching',
      owner: 'Unassigned',
      targetQuarter: 'Q3 2026',
      aiJustification: '',
      justificationLoading: false,
      notes: '',
    };
    setItems((prev) => {
      const ranked = [...prev, newItem].map((item, idx) => ({ ...item, rank: idx + 1 }));
      // Save new item to DB
      const finalItem = ranked[ranked.length - 1];
      saveItemToDB(finalItem).catch(() => {});
      // Schedule parent notification after render to avoid "update during render" error
      setTimeout(() => onItemsChange?.(ranked), 0);
      return ranked;
    });
  }, [onItemsChange]);

  const handleGenerateJustification = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;

      handleUpdate(id, { justificationLoading: true });

      try {
        const res = await getChatCompletion(
          'GEMINI',
          'gemini/gemini-2.5-flash',
          [
            {
              role: 'system',
              content: 'You are a roofing market expansion strategist for NextRoof 360. Write a single concise sentence (max 20 words) explaining why this city is ranked at its current priority position for expansion. Be specific about market opportunity.',
            },
            {
              role: 'user',
              content: `City: ${item.city}, ${item.state}
Priority Rank: #${item.rank} of ${items.length}
Stage: ${item.stage}
Target Quarter: ${item.targetQuarter}

Write a one-sentence AI justification for this ranking.`,
            },
          ],
          { max_tokens: 60, temperature: 0.5 }
        );
        const text = res?.choices?.[0]?.message?.content?.trim() ?? '';
        handleUpdate(id, { aiJustification: text, justificationLoading: false });
      } catch {
        handleUpdate(id, { justificationLoading: false });
      }
    },
    [items, handleUpdate]
  );

  const handleExportPDF = () => {
    setExporting(true);
    setTimeout(() => {
      exportToPDF(items);
      setExporting(false);
    }, 300);
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.07)', border: '1px solid #E5E7EB' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#F3F4F6' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}
          >
            <Icon name="ListBulletIcon" size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold" style={{ color: '#1C2E5E' }}>Expansion Priority List</h2>
            <p className="text-xs" style={{ color: '#9CA3AF' }}>
              Drag to reorder · Click ✦ for AI justification · Track stage &amp; ownership
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-xs px-3 py-1 rounded-full font-semibold"
            style={{ background: '#F3F4F6', color: '#374151' }}
          >
            {items.length} Markets
          </span>
          <button
            onClick={handleExportPDF}
            disabled={exporting || items.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all hover:opacity-90 disabled:opacity-50"
            style={{ background: '#1C2E5E', color: '#fff' }}
          >
            {exporting ? (
              <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
            ) : (
              <Icon name="ArrowDownTrayIcon" size={13} />
            )}
            Export PDF
          </button>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Stage summary */}
        <StageSummaryBar items={items} />

        {/* Drag hint */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
          style={{ background: '#F9FAFB', border: '1px solid #F3F4F6', color: '#9CA3AF' }}
        >
          <Icon name="Bars3Icon" size={12} />
          Drag rows to reorder priority · Click ✦ to generate Gemini justification · Click ▾ to expand details
        </div>

        {/* Items list */}
        {dbLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-2xl p-4 animate-pulse" style={{ background: '#F9FAFB', border: '1.5px solid #E5E7EB' }}>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg animate-pulse" style={{ background: '#E5E7EB' }} />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 rounded-full animate-pulse" style={{ background: '#E5E7EB', width: '40%' }} />
                    <div className="h-2.5 rounded-full animate-pulse" style={{ background: '#F3F4F6', width: '60%' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            className="rounded-2xl p-8 flex flex-col items-center justify-center text-center"
            style={{ background: '#F9FAFB', border: '2px dashed #E5E7EB' }}
          >
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
              style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)' }}
            >
              <Icon name="ListBulletIcon" size={24} style={{ color: '#E89A3C' }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: '#374151' }}>No markets in your priority list yet</p>
            <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
              Add markets below or save from Market Search above
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item, index) => (
              <PriorityItemRow
                key={item.id}
                item={item}
                isDragging={dragIndex === index}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(index)}
                onUpdate={(updates) => handleUpdate(item.id, updates)}
                onDelete={() => handleDelete(item.id)}
                onGenerateJustification={() => handleGenerateJustification(item.id)}
              />
            ))}
          </div>
        )}

        {/* Add market form */}
        <AddMarketForm onAdd={handleAdd} />
      </div>
    </div>
  );
}
